// Squares presets per league: the §4 table of Squares Redesign v3.1.1 as
// data, minus PGA, NASCAR and custom leagues (owner, 2026-11-01).
//
// Three preset families, all per league: game presets (which events), axes
// presets (what the two numbers mean) and checkpoint presets (when a winner
// is read). contest-api serves this table from /squares/presets-get, and
// every other Squares rule reads it from here, so there is one copy.
//
// Slot derivations (resolveGameRule.ts applies them, in the feed timezone):
// SNF = Sunday kickoff at or after 7:00 PM ET; MNF = any Monday game; TNF =
// Thursday excluding Thanksgiving; Thanksgiving = 4th Thursday of November;
// Christmas = Dec 25. Slot presets cover the regular season only; Playoffs
// covers the postseason.

import type {
  typeSquaresAxesKind,
  typeSquaresCheckpointKey,
  typeSquaresGamePresetKey,
  typeSquaresGridSize,
  typeSquaresGridType,
  typeSquaresLeagueKey,
} from "./types";

export type typeSquaresGamePreset = {
  presetKey: typeSquaresGamePresetKey;
  label: string;
  /** The grid types that may use this preset. `oneGame` is Classic only. */
  gridTypes: readonly typeSquaresGridType[];
  /** The preview offers the optional team filter. */
  isTeamFilterable: boolean;
  /** Zero matches still allows Create (the season is not scheduled yet). */
  allowsZeroMatches: boolean;
};

export type typeSquaresAxesPreset = {
  kind: typeSquaresAxesKind;
  label: string;
  gridTypes: readonly typeSquaresGridType[];
};

export type typeSquaresCheckpointPreset = {
  checkpointKey: typeSquaresCheckpointKey;
  label: string;
  /** Final is the only default, and it cannot be turned off. */
  isAlwaysOn: boolean;
};

export type typeSquaresLeaguePresets = {
  leagueKey: typeSquaresLeagueKey;
  /** The league can end a game tied, so Winner / Loser falls back to Home / Away. */
  allowsTies: boolean;
  defaultGridSize: typeSquaresGridSize;
  /** The feed timezone dates and slots are read in. */
  timeZone: string;
  gamePresets: readonly typeSquaresGamePreset[];
  axesPresets: readonly typeSquaresAxesPreset[];
  checkpoints: readonly typeSquaresCheckpointPreset[];
};

const BOTH: readonly typeSquaresGridType[] = ["classic", "shared"];
const CLASSIC: readonly typeSquaresGridType[] = ["classic"];
const EASTERN = "America/New_York";

function game(
  presetKey: typeSquaresGamePresetKey,
  label: string,
  options: {
    gridTypes?: readonly typeSquaresGridType[];
    isTeamFilterable?: boolean;
    allowsZeroMatches?: boolean;
  } = {},
): typeSquaresGamePreset {
  return {
    presetKey,
    label,
    gridTypes: options.gridTypes ?? BOTH,
    isTeamFilterable: options.isTeamFilterable ?? true,
    allowsZeroMatches: options.allowsZeroMatches ?? true,
  };
}

const ONE_GAME = game("oneGame", "One game", {
  gridTypes: CLASSIC,
  isTeamFilterable: false,
  allowsZeroMatches: false,
});
const ALL_GAMES = game("allGames", "All games");
const TEAM = game("team", "Team", { isTeamFilterable: false });
const PLAYOFFS = game("playoffs", "Playoffs");
const CHRISTMAS = game("christmas", "Christmas");
const CUSTOM_DATES = game("customDates", "Custom dates", {
  allowsZeroMatches: false,
});

const TEAM_SPORT_AXES: readonly typeSquaresAxesPreset[] = [
  { kind: "teamNames", label: "Team names", gridTypes: CLASSIC },
  { kind: "winnerLoser", label: "Winner / Loser", gridTypes: BOTH },
  { kind: "homeAway", label: "Home / Away", gridTypes: BOTH },
  { kind: "teamOpponent", label: "Team / Opponent", gridTypes: BOTH },
  { kind: "custom", label: "Custom", gridTypes: BOTH },
];

const FINAL: typeSquaresCheckpointPreset = {
  checkpointKey: "final",
  label: "Final",
  isAlwaysOn: true,
};

function checkpoint(
  checkpointKey: typeSquaresCheckpointKey,
  label: string,
): typeSquaresCheckpointPreset {
  return { checkpointKey, label, isAlwaysOn: false };
}

const QUARTERS: readonly typeSquaresCheckpointPreset[] = [
  checkpoint("q1", "Q1"),
  checkpoint("half", "Half"),
  checkpoint("q3", "Q3"),
  FINAL,
];

const COLLEGE_FOOTBALL_GAMES: readonly typeSquaresGamePreset[] = [
  ONE_GAME,
  ALL_GAMES,
  game("conference", "Conference"),
  TEAM,
  game("bowls", "Bowls"),
  game("cfp", "College Football Playoff"),
  CUSTOM_DATES,
];

const SOCCER_GAMES: readonly typeSquaresGamePreset[] = [
  game("oneGame", "One match", {
    gridTypes: CLASSIC,
    isTeamFilterable: false,
    allowsZeroMatches: false,
  }),
  ALL_GAMES,
  TEAM,
  game("matchdayRange", "Matchday range", { allowsZeroMatches: false }),
  CUSTOM_DATES,
];

const SOCCER_CHECKPOINTS: readonly typeSquaresCheckpointPreset[] = [
  checkpoint("half", "Half"),
  { checkpointKey: "final", label: "Full time", isAlwaysOn: true },
];

export const SQUARES_LEAGUE_PRESETS: Readonly<
  Record<typeSquaresLeagueKey, typeSquaresLeaguePresets>
> = {
  "football-nfl": {
    leagueKey: "football-nfl",
    allowsTies: true,
    defaultGridSize: 100,
    timeZone: EASTERN,
    gamePresets: [
      ONE_GAME,
      ALL_GAMES,
      game("tnf", "TNF"),
      game("snf", "SNF"),
      game("mnf", "MNF"),
      game("thanksgiving", "Thanksgiving"),
      CHRISTMAS,
      PLAYOFFS,
      CUSTOM_DATES,
    ],
    axesPresets: TEAM_SPORT_AXES,
    checkpoints: QUARTERS,
  },
  "football-fbs": {
    leagueKey: "football-fbs",
    allowsTies: false,
    defaultGridSize: 100,
    timeZone: EASTERN,
    gamePresets: COLLEGE_FOOTBALL_GAMES,
    axesPresets: TEAM_SPORT_AXES,
    checkpoints: QUARTERS,
  },
  "football-fcs": {
    leagueKey: "football-fcs",
    allowsTies: false,
    defaultGridSize: 100,
    timeZone: EASTERN,
    gamePresets: COLLEGE_FOOTBALL_GAMES,
    axesPresets: TEAM_SPORT_AXES,
    checkpoints: QUARTERS,
  },
  "basketball-nba": {
    leagueKey: "basketball-nba",
    allowsTies: false,
    defaultGridSize: 100,
    timeZone: EASTERN,
    gamePresets: [ONE_GAME, ALL_GAMES, CHRISTMAS, TEAM, PLAYOFFS, CUSTOM_DATES],
    axesPresets: TEAM_SPORT_AXES,
    checkpoints: QUARTERS,
  },
  "basketball-ncaab": {
    leagueKey: "basketball-ncaab",
    allowsTies: false,
    defaultGridSize: 100,
    timeZone: EASTERN,
    gamePresets: [
      ONE_GAME,
      ALL_GAMES,
      game("ncaaTournament", "NCAA Tournament"),
      game("conferenceTournaments", "Conference tournaments"),
      TEAM,
      CUSTOM_DATES,
    ],
    axesPresets: TEAM_SPORT_AXES,
    // NCAAB Half is admin-added until a live event confirms the feed's half
    // fields (decided 2026-11-01). The checkpoint is offered; the scoring job
    // skips it for this league.
    checkpoints: [checkpoint("half", "Half"), FINAL],
  },
  "hockey-nhl": {
    leagueKey: "hockey-nhl",
    allowsTies: false,
    defaultGridSize: 25,
    timeZone: EASTERN,
    gamePresets: [ONE_GAME, ALL_GAMES, TEAM, PLAYOFFS, CUSTOM_DATES],
    axesPresets: TEAM_SPORT_AXES,
    checkpoints: [checkpoint("p1", "P1"), checkpoint("p2", "P2"), FINAL],
  },
  "baseball-mlb": {
    leagueKey: "baseball-mlb",
    allowsTies: false,
    defaultGridSize: 25,
    timeZone: EASTERN,
    gamePresets: [
      ONE_GAME,
      ALL_GAMES,
      TEAM,
      game("openingDay", "Opening Day"),
      PLAYOFFS,
      game("worldSeries", "World Series"),
      CUSTOM_DATES,
    ],
    axesPresets: TEAM_SPORT_AXES,
    checkpoints: [
      checkpoint("after3", "After 3rd"),
      checkpoint("after5", "After 5th"),
      checkpoint("after7", "After 7th"),
      FINAL,
    ],
  },
  "soccer-mls": {
    leagueKey: "soccer-mls",
    allowsTies: true,
    defaultGridSize: 25,
    timeZone: "UTC",
    gamePresets: SOCCER_GAMES,
    axesPresets: TEAM_SPORT_AXES,
    checkpoints: SOCCER_CHECKPOINTS,
  },
  "soccer-epl": {
    leagueKey: "soccer-epl",
    allowsTies: true,
    defaultGridSize: 25,
    timeZone: "UTC",
    gamePresets: SOCCER_GAMES,
    axesPresets: TEAM_SPORT_AXES,
    checkpoints: SOCCER_CHECKPOINTS,
  },
};

/** The presets for a league, or null when Squares does not run in it. */
export function getSquaresLeaguePresets(
  leagueKey: string,
): typeSquaresLeaguePresets | null {
  return isSquaresLeagueKey(leagueKey)
    ? SQUARES_LEAGUE_PRESETS[leagueKey]
    : null;
}

export function isSquaresLeagueKey(
  leagueKey: string,
): leagueKey is typeSquaresLeagueKey {
  return Object.hasOwn(SQUARES_LEAGUE_PRESETS, leagueKey);
}
